<template>
  <vs-popup :title="' '" :active.sync="localActive" class="configure-popup succession-wizard">
    <div class="cfg">
      <button class="cfg__close-btn" @click="closeModal">
        <i class="feather icon-x"></i>
      </button>

      <!-- Progress Dots -->
       <div class="wiz__dots">
        <div class="wiz__dot" :class="{ 'wiz__dot--done': step > 1, 'wiz__dot--current': step === 1 }">
          <span><i v-if="step > 1" class="feather icon-check"></i><template v-else>1</template></span>
          <p>Org Details</p>
        </div>
        <div class="wiz__dot-line" :class="{ 'wiz__dot-line--active': step >= 2 }"></div>
        
        <div class="wiz__dot" :class="{ 'wiz__dot--done': step > 2, 'wiz__dot--current': step === 2 }">
          <span><i v-if="step > 2" class="feather icon-check"></i><template v-else>2</template></span>
          <p>Framework</p>
        </div>
        <div class="wiz__dot-line" :class="{ 'wiz__dot-line--active': step >= 3 }"></div>

        <div class="wiz__dot" :class="{ 'wiz__dot--done': step > 3, 'wiz__dot--current': step === 3 }">
          <span><i v-if="step > 3" class="feather icon-check"></i><template v-else>3</template></span>
          <p>Surveys</p>
        </div>
        <div class="wiz__dot-line" :class="{ 'wiz__dot-line--active': step >= 4 }"></div>
        
        <div class="wiz__dot" :class="{ 'wiz__dot--done': step > 4, 'wiz__dot--current': step === 4 }">
          <span><i v-if="step > 4" class="feather icon-check"></i><template v-else>4</template></span>
          <p>Credits</p>
        </div>
        <div class="wiz__dot-line" :class="{ 'wiz__dot-line--active': step >= 5 }"></div>
        
        <div class="wiz__dot" :class="{ 'wiz__dot--current': step === 5 }">
          <span>5</span>
          <p>Launch</p>
        </div>
      </div>

      <div class="wiz__step-scrollable">
        
        <!-- STEP 1: Organization Details -->
        <div v-if="step === 1" class="wiz__step">
          <div class="wiz__hero">
            <h3 class="wiz__title">Coach Platform Setup</h3>
            <p class="wiz__subtitle">Verify your organization details and set up your admin credentials for the Coaching platform.</p>
          </div>

          <div class="form-grid">
            <div class="wiz__input-group">
              <label class="wiz__label">Organization Name</label>
              <input type="text" class="wiz__input" v-model="form.orgName" placeholder="e.g. GetBoarded" />
            </div>
            
            <div class="wiz__input-group">
              <label class="wiz__label">Organization Type *</label>
              <select class="wiz__input" v-model="form.orgType">
                <option value="" disabled>Select Type</option>
                <option value="Business">Business</option>
                <option value="Academic">Academic</option>
                <option value="Recruiter">Recruiter</option>
              </select>
            </div>

            <div class="wiz__input-group">
              <label class="wiz__label">Coach Admin Email *</label>
              <input type="email" class="wiz__input" v-model="form.adminEmail" placeholder="admin@organization.com" />
            </div>

            <div class="wiz__input-group">
              <label class="wiz__label">Coach Admin Password *</label>
              <input type="password" class="wiz__input" v-model="form.adminPassword" placeholder="Create a secure password" />
            </div>

            <div class="wiz__input-group">
              <label class="wiz__label">Primary Theme Color</label>
              <div class="color-picker-wrap">
                <input type="color" class="color-box" v-model="form.primaryColor" />
                <input type="text" class="wiz__input" v-model="form.primaryColor" />
              </div>
            </div>

            <div class="wiz__input-group">
              <label class="wiz__label">Secondary Theme Color</label>
              <div class="color-picker-wrap">
                <input type="color" class="color-box" v-model="form.secondaryColor" />
                <input type="text" class="wiz__input" v-model="form.secondaryColor" />
              </div>
            </div>
          </div>
        </div>

        <!-- STEP 2: Choose Frameworks -->
        <div v-if="step === 2" class="wiz__step">
          <div class="wiz__hero">
            <h3 class="wiz__title">Select Coaching Frameworks</h3>
            <p class="wiz__subtitle">Choose one or more frameworks you want for your organization.</p>
          </div>

          <div class="framework-grid">
            <div 
                v-for="fw in availableFrameworks" 
                :key="fw.id" 
                class="fw-card" 
                :class="{ 'fw-card--active': form.selectedFrameworks.includes(fw.id) }"
                @click="toggleFramework(fw.id)"
                >
                <div class="fw-card__header-top">
                  <div class="fw-card__icon"><i :class="'feather ' + fw.icon"></i></div>
                  
                  <!-- SHOW OWNED BADGE IF APPLICABLE -->
                  <div v-if="ownedFrameworks[fw.id]" class="fw-card__owned-badge">
                    <i class="feather icon-check-circle"></i> Owned ({{ ownedFrameworks[fw.id].used }}/{{ ownedFrameworks[fw.id].total }} used)
                  </div>
                </div>
                
                <h4>{{ fw.name }}</h4>
                <p class="fw-card__desc">{{ fw.desc }}</p>
                <div class="fw-card__best-for">
                  <strong>Best for:</strong> {{ fw.bestFor }}
                </div>
            </div>
          </div>
        </div>

        <!-- STEP 3: Survey Settings -->
        <div v-if="step === 3" class="wiz__step">
          <div class="wiz__hero">
            <h3 class="wiz__title">Survey & Feedback Settings</h3>
            <p class="wiz__subtitle">Choose how often we should send coaching, feedback, and motivation check-ins during this plan.</p>
          </div>

          <div class="form-grid">
            <div class="wiz__input-group">
              <label class="wiz__label">
                Coaching Survey (No. of days) *
                <i class="feather icon-info tooltip-icon" title="Checks how the participant is experiencing the coaching or mentoring process."></i>
              </label>
              <input type="number" class="wiz__input" v-model.number="form.coachingSurvey" min="0" placeholder="e.g. 30" />
            </div>

            <div class="wiz__input-group">
              <label class="wiz__label">
                Motivation Survey (No. of days) *
                <i class="feather icon-info tooltip-icon" title="Checks whether the participant still feels motivated, sees progress, and has opportunities to practice."></i>
              </label>
              <input type="number" class="wiz__input" v-model.number="form.motivationSurvey" min="0" placeholder="e.g. 30" />
            </div>

            <div class="wiz__input-group">
              <label class="wiz__label">
                Feedback Frequency (No. of days) *
                <i class="feather icon-info tooltip-icon" title="Collects input from advisors or selected reviewers on visible progress and skill development."></i>
              </label>
              <input type="number" class="wiz__input" v-model.number="form.feedbackFrequency" min="0" placeholder="e.g. 15" />
            </div>
          </div>
        </div>

        <!-- STEP 4: User Credits -->
        <div v-if="step === 4" class="wiz__step">
            <div class="wiz__hero">
                <h3 class="wiz__title">Assign Participant Credits</h3>
                <p class="wiz__subtitle">Set how many people will go through each selected coaching plan.</p>
            </div>

            <!-- ONLY SHOW THIS TOGGLE IF MULTIPLE FRAMEWORKS ARE SELECTED -->
            <div 
                v-if="form.selectedFrameworks.length > 1" 
                class="equalize-toggle" 
                @click="form.equalizeSeats = !form.equalizeSeats"
            >
                <div class="toggle-box" :class="{ 'toggle-box--active': form.equalizeSeats }">
                <i v-if="form.equalizeSeats" class="feather icon-check"></i>
                </div>
                <span>Apply the same participant count to all selected frameworks</span>
            </div>

            <!-- Note: If only 1 framework is selected, we force it to behave like individual selection -->
            <div v-if="form.equalizeSeats && form.selectedFrameworks.length > 1" class="credit-row credit-row--master">
                <span class="fw-name">Participants per framework</span>
                <div class="wiz__size-control">
                <button class="wiz__size-btn" @click="form.baseSeats = Math.max(1, form.baseSeats - 1)"><i class="feather icon-minus"></i></button>
                <input type="number" class="wiz__size-input" v-model.number="form.baseSeats" min="1" />
                <button class="wiz__size-btn" @click="form.baseSeats++"><i class="feather icon-plus"></i></button>
                </div>
            </div>

          <div v-else class="credit-list">
            <div v-for="fwId in form.selectedFrameworks" :key="fwId" class="credit-row">
              <span class="fw-name"><i :class="'feather ' + getFramework(fwId).icon"></i> {{ getFramework(fwId).name }}</span>
              <div class="wiz__size-control">
                <button class="wiz__size-btn" @click="form.seats[fwId] = Math.max(1, (form.seats[fwId] || 1) - 1)"><i class="feather icon-minus"></i></button>
                <input type="number" class="wiz__size-input" v-model.number="form.seats[fwId]" min="1" />
                <button class="wiz__size-btn" @click="form.seats[fwId] = (form.seats[fwId] || 1) + 1"><i class="feather icon-plus"></i></button>
              </div>
            </div>
          </div>
        </div>

        <!-- STEP 5: Checkout & Launch -->
        <div v-if="step === 5" class="wiz__step">
          <div class="wiz__hero">
            <h3 class="wiz__title">Review & Pay</h3>
            <p class="wiz__subtitle">Review your succession coaching plan details.</p>
          </div>

          <div class="wiz__summary">
            <h4 class="wiz__summary-title">Order Summary</h4>
            <div class="wiz__summary-row">
              <span><i class="feather icon-briefcase"></i> {{ form.orgName || 'Your Organization' }}</span>
              <span class="tag">{{ form.orgType }}</span>
            </div>
            <div class="wiz__summary-divider"></div>
            
            <div v-for="fwId in form.selectedFrameworks" :key="fwId" class="wiz__summary-row">
              <span>{{ getFramework(fwId).name }} Plan (&euro;{{ getFramework(fwId).price }}/user)</span>
              <span>{{ getSeatsFor(fwId) }} participants</span>
            </div>

            <div class="wiz__summary-divider"></div>
            <div class="wiz__summary-row wiz__summary-row--total">
              <span>Total Plan Cost</span>
              <span class="wiz__pricing-total">&euro;{{ totalPrice.toFixed(2) }}</span>
            </div>
          </div>

          <div class="wiz__payment">
            <div class="wiz__payment-header">
              <i class="feather icon-credit-card"></i>
              <span>Payment Details</span>
            </div>
            <!-- Dummy Stripe Element for UI purposes -->
            <div class="wiz__stripe-card">
              <div class="dummy-card-element">
                 <span class="dummy-placeholder">Card number</span>
                 <div class="dummy-right">
                    <span>MM/YY</span>
                    <span>CVC</span>
                 </div>
              </div>
            </div>
          </div>

        </div>

      </div>

      <!-- Footer Actions -->
      <div class="wiz__footer">
  <button v-if="isAddingCredits ? step > 4 : step > (hasExistingOrg ? 2 : 1)" class="wiz__back" @click="step--">
    <i class="feather icon-arrow-left"></i> Back
  </button>
  <div v-else></div>

        <button class="wiz__next" :class="{ 'wiz__next--ready': step === 4 }" @click="handleNext" :disabled="!canProceed">
          <template v-if="isLoading">
            <i class="feather icon-loader fa-spin"></i> Processing...
          </template>
          <template v-else-if="step < 4">
            Continue <i class="feather icon-arrow-right"></i>
          </template>
          <template v-else>
            Pay & Launch <i class="feather icon-check"></i>
          </template>
        </button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import axios from 'axios';
export default {
  name: 'SuccessionPlanWizard',
  props: {
    active: { type: Boolean, default: false },
    orgData: { type: Object, default: () => ({}) },
    isAddingCredits: { type: Boolean, default: false },
    targetPlan: { type: Object, default: null }
  },
  data() {
    return {
      step: 1,
      isLoading: false,
      existingCompanyId: null,
      hasExistingOrg: false,
      ownedFrameworks: {},
      availableFrameworks: [
        { 
          id: 'behavioural', 
          name: 'Behaviour & Leadership', 
          desc: 'Build stronger workplace habits, communication, and leadership behaviors aligned with your company values. Best for succession planning, soft-skill development, and people growth.', 
          bestFor: 'Teams, Managers, Future Leaders, Personal Development',
          icon: 'icon-users',
          price: 70 // Added
        },
        { 
          id: 'founder', 
          name: 'Founder Journey', 
          desc: 'Coaching for startup founders and leadership teams to align people, priorities, and functions at each stage of growth. Best for startups moving from product to market to scale.', 
          bestFor: 'Founders, Startup Executives, Leadership Teams',
          icon: 'icon-award',
          price: 90 // Added
        },
        { 
          id: 'performance', 
          name: 'Performance Evaluation', 
          desc: 'Use goals, KPIs, and performance reviews to support career growth and better role clarity. Best for contributors, consultants, and managers who want measurable development.', 
          bestFor: 'Individuals, Mananging Leads, Performance-evaluation of Teams',
          icon: 'icon-trending-up',
          price: 80 // Added
        },
        { 
          id: 'ecosystem', 
          name: 'Ecosystem & Community', 
          desc: 'Grow leaders, partnerships, and communities through a structured ecosystem-building approach. Best for networks, innovation communities, hubs, and collaboration-driven programs.', 
          bestFor: 'Ecosystem Builders, Community Leaders, Program Operators',
          icon: 'icon-globe',
          price: 100 // Added
        }
      ],
      form: {
        orgName: '',
        orgType: '',
        adminEmail: '',
        adminPassword: '',
        primaryColor: '#FBBF15',
        secondaryColor: '#E8AE08',
        selectedFrameworks: [],
        equalizeSeats: true,
        baseSeats: 10,
        seats: {},
        coachingSurvey: 30,    // Default value
        motivationSurvey: 30,  // Default value
        feedbackFrequency: 15, // Default value
      }
    };
  },
  computed: {
    localActive: {
      get() { return this.active; },
      set(val) { this.$emit('update:active', val); }
    },
    canProceed() {
      if (this.step === 1) return this.form.orgType && this.form.adminEmail && this.form.adminPassword;
      if (this.step === 2) return this.form.selectedFrameworks.length > 0;
      if (this.step === 3) return this.form.coachingSurvey !== '' && this.form.motivationSurvey !== '' && this.form.feedbackFrequency !== '';
      if (this.step === 4) return true;
      if (this.step === 5) return true;
      return false;
    },
    totalPrice() {
      return this.form.selectedFrameworks.reduce((total, fwId) => {
        const fw = this.getFramework(fwId);
        const pricePerSeat = fw.price || 0;
        const seats = this.getSeatsFor(fwId);
        return total + (seats * pricePerSeat);
      }, 0);
    }
  },
  watch: {
    async active(newVal) {
      if (newVal) {
        if (this.isAddingCredits && this.targetPlan) {
        this.step = 4; // Bypasses steps 1-3, jumps to credits step
        this.isLoading = false;
        this.form.selectedFrameworks = [this.targetPlan.frameworkId || this.targetPlan.framework_id];
        this.$set(this.form.seats, this.form.selectedFrameworks[0], 5); // Default additional seats increment
        this.form.baseSeats = 5;
        this.form.equalizeSeats = false;
        return;
      }

        this.step = 1; // Default to step 1 while loading
        this.isLoading = true;
        this.existingCompanyId = null;
        this.hasExistingOrg = false; // Add this to your data() block too!
        
        this.prefillData();
        
        // ⭐️ NEW ENHANCEMENT: Check if Org already exists in Python DB
        if (this.orgData && this.orgData._id) {
            try {
                const pyApiUrl = process.env.VUE_APP_API_URL;
                const response = await fetch(`${pyApiUrl}/api/v2/coach-plans/${this.orgData._id}`);
                const pyData = await response.json();
                
                // If plans exist, it means the Org is already set up!
                if (pyData.data && pyData.data.length > 0) {
                    this.hasExistingOrg = true;
                    this.step = 2; // SKIP STEP 1!
                    
                    // NEW: Map owned frameworks so UI can show badges
                    this.ownedFrameworks = {};
                    pyData.data.forEach(plan => {
                        let usedCount = 0;
                        try { usedCount = typeof plan.assigned_users === 'string' ? JSON.parse(plan.assigned_users).length : plan.assigned_users.length; } catch(e) {}
                        
                        this.$set(this.ownedFrameworks, plan.framework_id, {
                            total: plan.total_credits,
                            used: usedCount || 0,
                            board_id: plan.board_id // 👈 ADD THIS LINE
                        });
                    });

                    // Fetch MySQL Company ID
                    const feathersApiUrl = 'https://api.coach.getboarded.com';
                    const compRes = await fetch(`${feathersApiUrl}/company?name=${encodeURIComponent(this.orgData.name)}`);
                    const compData = await compRes.json();
                    if (compData.data && compData.data.length > 0) {
                        this.existingCompanyId = compData.data[0].id;
                    }
                }

            } catch (e) {
                console.warn("Could not verify existing Coach Org status", e);
            }
        }
        
        this.isLoading = false;
      }
    },
  },
  methods: {
    prefillData() {
      if (this.orgData) {
        this.form.orgName = this.orgData.name || '';
        this.form.primaryColor = this.orgData.primaryThemeColor || '#059669';
        this.form.secondaryColor = this.orgData.secondaryThemeColor || '#34D399';
        // Reset required fields
        this.form.orgType = '';
        this.form.adminEmail = '';
        this.form.adminPassword = '';
        this.form.selectedFrameworks = [];
      }
    },
    getFramework(id) {
      return this.availableFrameworks.find(f => f.id === id) || {};
    },
    getSeatsFor(id) {
      if (this.form.equalizeSeats && this.form.selectedFrameworks.length > 1) {
        return this.form.baseSeats;
      }
      return this.form.seats[id] || 1;
    },
    toggleFramework(id) {
      if (this.form.selectedFrameworks.includes(id)) {
        this.form.selectedFrameworks = this.form.selectedFrameworks.filter(fw => fw !== id);
      } else {
        this.form.selectedFrameworks.push(id);
        if (!this.form.seats[id]) this.$set(this.form.seats, id, this.form.baseSeats);
      }
    },
    closeModal() {
      this.localActive = false;
    },
    async handleNext() {
      if (this.step < 5) { // Updated to 5 steps
        this.step++;
      } else {
        await this.submitPaymentAndLaunch();
      }
    },
    async submitPaymentAndLaunch() {
      this.isLoading = true;
      
      const pyApiUrl = process.env.VUE_APP_API_URL; 
      
      try {
        // --- 1. HANDLE MYSQL ORG ---
        if (!this.hasExistingOrg) {
            console.log("CREATING NEW COACH DB ORG...");
            
            const mySqlPayload = {
              organization_name: this.form.orgName,
              admin_email: this.form.adminEmail,
              password: this.form.adminPassword, 
              organization_type: this.form.orgType,
              theme_color: this.form.primaryColor,
              secondary_color: this.form.secondaryColor,
              enterprise_type: 1, 
              coaching_survey_days: this.form.coachingSurvey.toString(),
              motivation_survey_days: this.form.motivationSurvey.toString(),
              feedback_frequency: this.form.feedbackFrequency.toString(),
              status: 'active',
              total_paid: this.totalPrice,
              mongo_org_id: this.orgData._id,
            };
            
            await axios.post('https://api.coach.getboarded.com/company', mySqlPayload);
        } else {
            console.log("ORG ALREADY EXISTS. UPDATING SURVEY FREQUENCIES...");
            
            if (this.existingCompanyId) {
                const mySqlUpdatePayload = {
                    coaching_survey_days: this.form.coachingSurvey.toString(),
                    motivation_survey_days: this.form.motivationSurvey.toString(),
                    feedback_frequency: this.form.feedbackFrequency.toString(),
                    mongo_org_id: this.orgData._id,
                };
                
                await axios.patch(`https://api.coach.getboarded.com/company/${this.existingCompanyId}`, mySqlUpdatePayload);
            }
        }

        // --- 2. CREATE SPECIFIC FRAMEWORK BOARDS & SAVE TO PYTHON ---
        const plansWithBoards = [];

        // Helper to generate the CORRECT columns for the specific Coach boards
        const getKanbanCols = (fwId) => {
          const getColBase = (title, description) => ({ title, description, colUsersData: [] });
          switch(fwId) {
              case 'behavioural':
                  return [getColBase('Get Boarded', 'Onboarding'), getColBase('Awareness & Assessment', 'Initial evaluation'), getColBase('Goal Definition', 'Setting targets'), getColBase('Active Coaching', 'Engaged in sessions'), getColBase('Progress Review', 'Evaluating feedback'), getColBase('Integration', 'Successfully integrated')];
              case 'founder':
                  return [getColBase('Get Boarded', 'Onboarding'), getColBase('Vision Alignment', 'Aligning goals'), getColBase('Leadership Scaling', 'Developing skills'), getColBase('Strategic Planning', 'Strategy mapping'), getColBase('Execution & Milestones', 'Tracking growth'), getColBase('Milestone Achieved', 'Successful execution')];
              case 'performance':
                  return [getColBase('Get Boarded', 'Onboarding'), getColBase('KPI Setting', 'Defining metrics'), getColBase('Continuous Tracking', 'Ongoing logging'), getColBase('Mid-Cycle Review', 'Feedback'), getColBase('Final Evaluation', 'End of cycle'), getColBase('Completed', 'Evaluation closed')];
              case 'ecosystem':
                  return [getColBase('Get Boarded', 'Onboarding'), getColBase('Network Mapping', 'Identifying stakeholders'), getColBase('Engagement Strategy', 'Planning interactions'), getColBase('Relationship Building', 'Active networking'), getColBase('Value Creation', 'Mutual benefits'), getColBase('Sustained Ecosystem', 'Established network')];
              default:
                  return [getColBase('Get Boarded', 'Onboarding'), getColBase('Talent Identification', 'Nominated'), getColBase('Readiness Assessment', 'Gap analysis'), getColBase('Mentorship & Coaching', 'Active development'), getColBase('Shadowing', 'Role observation'), getColBase('Ready for Promotion', 'Fully prepared')];
          }
        };

        for (const fwId of this.form.selectedFrameworks) {
          const fwDetails = this.getFramework(fwId);
          const planName = fwDetails.name + ' Plan';
          const seats = this.getSeatsFor(fwId);

          let finalBoardId = null;

          // 👉 1. Check if we already own this framework and have an existing board
          if (this.ownedFrameworks[fwId] && this.ownedFrameworks[fwId].board_id) {
            finalBoardId = this.ownedFrameworks[fwId].board_id;
          } 
          // 👉 2. Or check if we are just adding credits to an existing target plan
          else if (this.isAddingCredits && this.targetPlan && (this.targetPlan.frameworkId === fwId || this.targetPlan.framework_id === fwId)) {
            finalBoardId = this.targetPlan.board_id || this.targetPlan.boardId;
          }

          // 👉 3. ONLY create a new board & columns if it does NOT exist yet
          if (!finalBoardId) {
            try {
              // A. Create the actual Workspace Board in GetBoarded
              const slug = planName.toLowerCase().replace(/ /g, "-").replace(/[^\w-]+/g, "") + "-" + Math.random().toString(36).substring(3);
              
              const boardRes = await this.$store.dispatch("boards/createBoard", {
                title: planName,
                boardId: 'custom-kanban-board',
                userId: this.$store.state.users.data._id,
                organizationId: this.orgData._id,
                numberOfUsers: seats,
                installedApps: [],
                paymentStatus: "paid",
                status: "active",
                boardView: "kanban",
                slug: slug
              });

              finalBoardId = boardRes.data._id || (boardRes.data.data && boardRes.data.data._id);

              // B. Initialize the CORRECT Kanban columns specific to the framework
              const kanbanCols = getKanbanCols(fwId);
              await this.$store.dispatch("boards/createKanban", { boardId: finalBoardId, cols: kanbanCols });

            } catch(boardErr) {
              console.error("Failed to create board for", planName, boardErr);
            }
          }

          // C. Add to payload passing the existing (or newly created) Board ID
          plansWithBoards.push({
            framework_id: fwId,
            plan_name: planName,
            total_credits: seats,
            board_id: finalBoardId 
          });
        }

        // --- 3. SEND TO PYTHON API ---
        const pythonPlansPayload = {
          org_id: this.orgData._id, 
          plans: plansWithBoards
        };

        console.log("SENDING NEW PLANS TO PYTHON DB:", pythonPlansPayload);
        await axios.post(`${pyApiUrl}/api/v2/coach-plans`, pythonPlansPayload);
        
        this.isLoading = false;
        this.$vs.notify({ color: 'success', title: 'Payment Successful', text: 'New Plans added successfully!' });
        this.closeModal();
        
        // Refresh the parent's dashboard so the new board appears instantly in "My Plans"
        this.$emit('plans-created');

        

      } catch (error) {
        this.isLoading = false;
        console.error("Error syncing organization data:", error);
        this.$vs.notify({ color: 'danger', title: 'Sync Error', text: 'Failed to sync with the platform.' });
      }
    }

  }
}
</script>

<style lang="scss" scoped>
$brand: #FBBF15; 
$brand-hover: #E8AE08;
$brand-light: #FFFBEB;
$brand-border: #FDE68A;
$brand-text: #D97706;

/* Reusing base popup styles from your main component */
.configure-popup {
  ::v-deep .vs-popup { width: 680px; max-width: 95vw; border-radius: 20px; overflow: visible !important; }
  ::v-deep .vs-popup--header { display: none; }
  ::v-deep .vs-popup--content { padding: 0 !important; margin: 0 !important; overflow: visible !important; }
}

.cfg { position: relative; text-align: left; background: white; border-radius: 20px; }
.cfg__close-btn { position: absolute; top: 1.25rem; right: 1.25rem; width: 32px; height: 32px; border-radius: 8px; background: #f3f4f6; border: none; color: #6b7280; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 50; }
.cfg__close-btn:hover { background: #e5e7eb; color: #111827; }

/* Dots */
.wiz__dots { display: flex; align-items: flex-start; justify-content: center; gap: 0; margin-bottom: 1rem; padding: 1.5rem 2.5rem 0; }
.wiz__dot { display: flex; flex-direction: column; align-items: center; gap: 5px; }
.wiz__dot span { width: 30px; height: 30px; border-radius: 50%; background: #f3f4f6; color: #d1d5db; font-size: 0.7rem; font-weight: 800; display: flex; align-items: center; justify-content: center; transition: all 0.35s ease; }
.wiz__dot p { font-size: 0.6rem; font-weight: 700; color: #d1d5db; text-transform: uppercase; letter-spacing: 0.06em; }
.wiz__dot--done span { background: $brand-light; color: $brand-text; }
.wiz__dot--done p { color: #9ca3af; }
.wiz__dot--current span { background: linear-gradient(135deg, $brand, $brand-hover); color: white; box-shadow: 0 3px 12px rgba($brand, 0.35); transform: scale(1.08); }
.wiz__dot--current p { color: #111827; }
.wiz__dot-line { width: 40px; height: 2px; background: #f3f4f6; margin-top: 14px; border-radius: 2px; }
.wiz__dot-line--active { background: $brand-border; }

.wiz__step-scrollable { max-height: 60vh; overflow-y: auto; padding: 0 2.5rem 1rem; }
.wiz__step-scrollable::-webkit-scrollbar { display: none; }
.wiz__hero { text-align: center; margin-bottom: 1.5rem; }
.wiz__title { font-size: 1.25rem; font-weight: 800; color: #111827; margin-bottom: 4px; }
.wiz__subtitle { font-size: 0.85rem; color: #9ca3af; max-width: 420px; margin: 0 auto; line-height: 1.5; }

/* Form Elements */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.wiz__input-group { display: flex; flex-direction: column; }
.wiz__label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; color: #6b7280; margin-bottom: 0.4rem; }
.wiz__input { padding: 0.65rem 0.875rem; border: 2px solid #e5e7eb; border-radius: 10px; font-size: 0.9rem; outline: none; background: white; font-family: inherit; }
.wiz__input:focus { border-color: $brand; box-shadow: 0 0 0 3px rgba(251, 191, 21, 0.15); }

.color-picker-wrap { display: flex; align-items: center; gap: 8px; }
.color-box { width: 40px; height: 40px; border: none; border-radius: 8px; cursor: pointer; background: transparent; padding: 0; }
.color-box::-webkit-color-swatch-wrapper { padding: 0; }
.color-box::-webkit-color-swatch { border: 2px solid #e5e7eb; border-radius: 8px; }

/* Framework Cards */
.framework-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.fw-card { position: relative; padding: 1.25rem; border: 2px solid #e5e7eb; border-radius: 12px; cursor: pointer; transition: all 0.2s; background: white; }
.fw-card:hover { border-color: $brand-border; background: $brand-light; transform: translateY(-2px); }
.fw-card--active { border-color: $brand; background: $brand-light; box-shadow: 0 4px 12px rgba($brand, 0.1); }
.fw-card__icon { width: 36px; height: 36px; border-radius: 8px; background: $brand-light; color: $brand-text; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; margin-bottom: 0.75rem; }
.fw-card h4 { font-size: 0.95rem; font-weight: 700; color: #111827; margin-bottom: 4px; }
.fw-card p { font-size: 0.75rem; color: #6b7280; line-height: 1.4; }

/* Credits controls */
.equalize-toggle { display: flex; align-items: center; gap: 10px; padding: 1rem; background: #f9fafb; border-radius: 10px; cursor: pointer; margin-bottom: 1.5rem; }
.toggle-box { width: 22px; height: 22px; border-radius: 6px; border: 2px solid #d1d5db; display: flex; align-items: center; justify-content: center; color: white; font-size: 0.7rem; }
.toggle-box--active { background: $brand; border-color: $brand; }
.equalize-toggle span { font-size: 0.85rem; font-weight: 600; color: #374151; }

.credit-list { display: flex; flex-direction: column; gap: 0.75rem; }
.credit-row { display: flex; align-items: center; justify-content: space-between; padding: 1rem; border: 1.5px solid #e5e7eb; border-radius: 10px; }
.credit-row--master { background: $brand-light; border-color: $brand-border; }
.fw-name { font-weight: 700; color: #111827; display: flex; align-items: center; gap: 8px; }
.fw-name i { color: $brand-text; }

.wiz__size-control { display: flex; align-items: center; background: white; border-radius: 10px; border: 2px solid #e5e7eb; overflow: hidden; }
.wiz__size-btn { width: 34px; height: 34px; border: none; background: transparent; color: #6b7280; cursor: pointer; font-size: 0.9rem; }
.wiz__size-btn:hover { background: #f3f4f6; color: #111827; }
.wiz__size-input { width: 50px; text-align: center; border: none; font-size: 1rem; font-weight: 800; outline: none; }
.wiz__size-input::-webkit-inner-spin-button { -webkit-appearance: none; }

/* Checkout Summary */
.wiz__summary { background: #FAFAFA; border-radius: 12px; padding: 1rem 1.25rem; margin-bottom: 1rem; border: 1px solid #f3f4f6; }
.wiz__summary-title { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; color: #9ca3af; margin-bottom: 0.75rem; }
.wiz__summary-row { display: flex; justify-content: space-between; align-items: center; font-size: 0.85rem; color: #374151; padding: 0.3rem 0; font-weight: 600; }
.wiz__summary-row .tag { background: #e5e7eb; font-size: 0.65rem; padding: 2px 6px; border-radius: 4px; }
.wiz__summary-divider { height: 1px; background: #e5e7eb; margin: 0.5rem 0; }
.wiz__summary-row--total { font-weight: 800; color: #111827; font-size: 0.95rem; padding-top: 0.5rem; }
.wiz__pricing-total { font-size: 1.2rem; color: $brand-text; }

.wiz__payment { border: 1.5px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
.wiz__payment-header { display: flex; align-items: center; gap: 8px; padding: 0.75rem 1rem; background: #f9fafb; border-bottom: 1px solid #f3f4f6; font-size: 0.8rem; font-weight: 700; color: #6b7280; text-transform: uppercase; }
.wiz__stripe-card { padding: 1rem; background: white; }
.dummy-card-element { display: flex; justify-content: space-between; padding: 0.75rem; border: 1.5px solid #e5e7eb; border-radius: 8px; color: #9ca3af; font-size: 0.9rem; }
.dummy-right { display: flex; gap: 1rem; }

/* Footer */
.wiz__footer { display: flex; justify-content: space-between; align-items: center; padding: 1rem 2.5rem 1.5rem; border-top: 1px solid #f3f4f6; }
.wiz__back { padding: 0.6rem 1rem; border-radius: 10px; border: none; background: #f3f4f6; color: #6b7280; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px; }
.wiz__back:hover { background: #e5e7eb; color: #111827; }
.wiz__next { padding: 0.6rem 1.5rem; border-radius: 10px; border: none; background: linear-gradient(135deg, $brand, $brand-hover); color: white; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
.wiz__next:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 14px rgba($brand, 0.3); }
.wiz__next:disabled { opacity: 0.5; cursor: not-allowed; }
.fw-card__header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.fw-card__owned-badge {
  font-size: 0.65rem;
  font-weight: 700;
  background: #ECFDF5;
  color: #059669;
  padding: 3px 8px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #A7F3D0;
}
.fw-card { 
  display: flex; 
  flex-direction: column; 
}
.fw-card__desc {
  margin-bottom: 0.75rem !important;
}
.fw-card__best-for {
  font-size: 0.7rem;
  color: #4b5563;
  background: #f9fafb;
  padding: 6px 10px;
  border-radius: 6px;
  margin-top: auto; /* Pushes the 'Best for' to the bottom equally on all cards */
  border: 1px solid #e5e7eb;
}
.fw-card__best-for strong {
  color: #111827;
}

/* --- New additions for Step 3 Tooltips --- */
.wiz__label {
  display: flex;
  align-items: center;
}
.tooltip-icon {
  margin-left: 6px;
  font-size: 0.8rem;
  color: #9ca3af;
  cursor: help;
  transition: color 0.2s;
}
.tooltip-icon:hover {
  color: #374151;
}
</style>